import React from 'react';
import { User } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const Characters: React.FC = () => {
  const { t, language } = useLanguage();

  const characters = [
    {
      name: 'Francis',
      actor: 'Christopher Kennedy Masterson',
      traits: {
        zh: ['叛逆不羁', '独立自主', '重视自由', '家庭情结'],
        en: ['Rebellious', 'Independent', 'Freedom-loving', 'Family-oriented'],
        ru: ['Мятежный', 'Независимый', 'Свободолюбивый', 'Семейный'],
        fr: ['Rebelle', 'Indépendant', 'Épris de liberté', 'Attaché à la famille'],
        de: ['Rebellisch', 'Unabhängig', 'Freiheitsliebend', 'Familienorientiert'],
        nl: ['Rebels', 'Onafhankelijk', 'Vrijheidslievend', 'Familiemens'],
        ms: ['Memberontak', 'Berdikari', 'Pencinta Kebebasan', 'Berorientasikan Keluarga']
      },
      memorableMoments: {
        zh: ['军校生活', '阿拉斯加工作', '牧场管理经历'],
        en: ['Military school life', 'Working in Alaska', 'Ranch management'],
        ru: ['Жизнь в военной школе', 'Работа на Аляске', 'Управление ранчо'],
        fr: ['La vie à l\'école militaire', 'Le travail en Alaska', 'La gestion du ranch'],
        de: ['Leben in der Militärschule', 'Arbeit in Alaska', 'Ranch-Management'],
        nl: ['Leven op de militaire school', 'Werken in Alaska', 'Beheer van de ranch'],
        ms: ['Kehidupan di sekolah tentera', 'Bekerja di Alaska', 'Pengurusan ladang']
      }
    },
    {
      name: 'Reese',
      actor: 'Justin Berfield',
      traits: {
        zh: ['烹饪天才', '冲动好斗', '重情重义', '简单直接'],
        en: ['Cooking Genius', 'Impulsive', 'Loyal', 'Straightforward'],
        ru: ['Кулинарный гений', 'Импульсивный', 'Верный', 'Прямолинейный'],
        fr: ['Génie culinaire', 'Impulsif', 'Loyal', 'Direct'],
        de: ['Kochgenie', 'Impulsiv', 'Loyal', 'Geradlinig'],
        nl: ['Kookgenie', 'Impulsief', 'Loyaal', 'Rechtdoorzee'],
        ms: ['Genius Memasak', 'Impulsif', 'Setia', 'Terus-terang']
      },
      memorableMoments: {
        zh: ['展现厨艺天赋', '参军经历', '为家人挺身而出'],
        en: ['Showing cooking talent', 'Military experience', 'Standing up for family'],
        ru: ['Проявление кулинарного таланта', 'Военный опыт', 'Защита семьи'],
        fr: ['Démonstration de talent culinaire', 'Expérience militaire', 'Défense de la famille'],
        de: ['Zeigen von Kochtalent', 'Militärerfahrung', 'Einstehen für die Familie'],
        nl: ['Tonen van kooktalent', 'Militaire ervaring', 'Opkomen voor familie'],
        ms: ['Menunjukkan bakat memasak', 'Pengalaman ketenteraan', 'Membela keluarga']
      }
    },
    {
      name: 'Malcolm',
      actor: 'Frankie Muniz',
      traits: {
        zh: ['天才智商', '直率诚实', '社交困扰', '责任感强'],
        en: ['Genius IQ', 'Honest', 'Socially Awkward', 'Responsible'],
        ru: ['Гениальный IQ', 'Честный', 'Социально неловкий', 'Ответственный'],
        fr: ['QI de génie', 'Honnête', 'Socialement maladroit', 'Responsable'],
        de: ['Genie-IQ', 'Ehrlich', 'Sozial unbeholfen', 'Verantwortungsvoll'],
        nl: ['Geniaal IQ', 'Eerlijk', 'Sociaal onhandig', 'Verantwoordelijk'],
        ms: ['IQ Genius', 'Jujur', 'Janggal Secara Sosial', 'Bertanggungjawab']
      },
      memorableMoments: {
        zh: ['加入天才班', '与家人共同面对生活困境', '最终被哈佛录取'],
        en: ['Joining the Krelboyne class', 'Facing life challenges with family', 'Getting accepted to Harvard'],
        ru: ['Поступление в класс для одаренных', 'Преодоление жизненных трудностей с семьей', 'Поступление в Гарвард'],
        fr: ['Intégration de la classe des surdoués', 'Affronter les défis de la vie en famille', 'Admission à Harvard'],
        de: ['Beitritt zur Hochbegabtenklasse', 'Bewältigung von Lebensherausforderungen mit der Familie', 'Aufnahme in Harvard'],
        nl: ['Toetreding tot de hoogbegaafdenklas', 'Levensuitdagingen aangaan met familie', 'Aangenomen worden op Harvard'],
        ms: ['Menyertai kelas berbakat', 'Menghadapi cabaran hidup bersama keluarga', 'Diterima masuk ke Harvard']
      }
    },
    {
      name: 'Dewey',
      actor: 'Erik Per Sullivan',
      traits: {
        zh: ['音乐天才', '富有想象力', '适应能力强', '独立思考'],
        en: ['Musical Prodigy', 'Imaginative', 'Adaptable', 'Independent Thinker'],
        ru: ['Музыкальный вундеркинд', 'С богатым воображением', 'Адаптивный', 'Независимый мыслитель'],
        fr: ['Prodige musical', 'Imaginatif', 'Adaptable', 'Penseur indépendant'],
        de: ['Musikalisches Wunderkind', 'Fantasievoll', 'Anpassungsfähig', 'Unabhängiger Denker'],
        nl: ['Muzikaal wonderkind', 'Fantasierijk', 'Aanpasbaar', 'Onafhankelijk denker'],
        ms: ['Prodigi Muzik', 'Imaginatif', 'Mudah Menyesuaikan Diri', 'Pemikir Bebas']
      },
      memorableMoments: {
        zh: ['发现音乐天赋', '自学钢琴', '创作歌剧'],
        en: ['Discovering musical talent', 'Teaching himself piano', 'Creating an opera'],
        ru: ['Открытие музыкального таланта', 'Самостоятельное обучение игре на пианино', 'Создание оперы'],
        fr: ['Découverte du talent musical', 'Apprentissage autodidacte du piano', 'Création d\'un opéra'],
        de: ['Entdeckung des musikalischen Talents', 'Selbststudium des Klaviers', 'Erschaffung einer Oper'],
        nl: ['Ontdekking van muzikaal talent', 'Zichzelf piano leren spelen', 'Een opera creëren'],
        ms: ['Menemui bakat muzik', 'Belajar piano sendiri', 'Mencipta opera']
      }
    },
    {
      name: 'Jamie',
      actor: 'James and Lukas Rodriguez',
      traits: {
        zh: ['适应力强', '天真可爱', '调皮捣蛋', '受宠爱的小弟'],
        en: ['Adaptable', 'Innocent', 'Mischievous', 'Beloved Youngest'],
        ru: ['Адаптивный', 'Невинный', 'Озорной', 'Любимый младший'],
        fr: ['Adaptable', 'Innocent', 'Espiègle', 'Benjamin adoré'],
        de: ['Anpassungsfähig', 'Unschuldig', 'Schelmisch', 'Geliebter Jüngster'],
        nl: ['Aanpasbaar', 'Onschuldig', 'Ondeugend', 'Geliefde jongste'],
        ms: ['Mudah Menyesuaikan Diri', 'Tidak Berdosa', 'Nakal', 'Anak Bongsu Tersayang']
      },
      memorableMoments: {
        zh: ['第四季出生', '与兄弟们的互动', '成为家庭新欢'],
        en: ['Born in Season 4', 'Interactions with siblings', 'Becoming family\'s new joy'],
        ru: ['Рождение в 4-м сезоне', 'Взаимодействие с братьями', 'Становление новой радостью семьи'],
        fr: ['Né dans la saison 4', 'Interactions avec les frères', 'Devenir la nouvelle joie de la famille'],
        de: ['Geboren in Staffel 4', 'Interaktionen mit Geschwistern', 'Wird zur neuen Freude der Familie'],
        nl: ['Geboren in seizoen 4', 'Interacties met broers', 'Wordt de nieuwe vreugde van de familie'],
        ms: ['Lahir dalam Musim 4', 'Interaksi dengan adik-beradik', 'Menjadi kegembiraan baru keluarga']
      }
    },
    {
      name: 'Lois',
      actor: 'Jane Kaczmarek',
      traits: {
        zh: ['意志坚强', '公平正直', '严格管教', '充满爱心'],
        en: ['Strong-willed', 'Fair', 'Strict', 'Loving'],
        ru: ['Волевая', 'Справедливая', 'Строгая', 'Любящая'],
        fr: ['Forte personnalité', 'Juste', 'Stricte', 'Aimante'],
        de: ['Willensstark', 'Fair', 'Streng', 'Liebevoll'],
        nl: ['Wilskrachtig', 'Eerlijk', 'Streng', 'Liefdevol'],
        ms: ['Berpendirian Teguh', 'Adil', 'Tegas', 'Penyayang']
      },
      memorableMoments: {
        zh: ['与孩子们的日常斗争', '坚持原则的教育方式', '在困境中保护家人'],
        en: ['Daily struggles with kids', 'Principled parenting', 'Protecting family in hardship'],
        ru: ['Ежедневная борьба с детьми', 'Принципиальное воспитание', 'Защита семьи в трудные времена'],
        fr: ['Luttes quotidiennes avec les enfants', 'Parentalité basée sur des principes', 'Protection de la famille dans l\'adversité'],
        de: ['Tägliche Auseinandersetzungen mit den Kindern', 'Prinzipientreue Erziehung', 'Familie in schweren Zeiten beschützen'],
        nl: ['Dagelijkse strijd met de kinderen', 'Principieel ouderschap', 'Familie beschermen in moeilijke tijden'],
        ms: ['Cabaran harian dengan anak-anak', 'Keibubapaan berprinsip', 'Melindungi keluarga dalam kesusahan']
      }
    },
    {
      name: 'Hal',
      actor: 'Bryan Cranston',
      traits: {
        zh: ['富有同理心', '热爱家庭', '随性而为', '创意十足'],
        en: ['Empathetic', 'Family-loving', 'Spontaneous', 'Creative'],
        ru: ['Эмпатичный', 'Семейный', 'Спонтанный', 'Креативный'],
        fr: ['Empathique', 'Familial', 'Spontané', 'Créatif'],
        de: ['Einfühlsam', 'Familienliebend', 'Spontan', 'Kreativ'],
        nl: ['Empathisch', 'Familiemens', 'Spontaan', 'Creatief'],
        ms: ['Berempati', 'Pencinta Keluarga', 'Spontan', 'Kreatif']
      },
      memorableMoments: {
        zh: ['与孩子们的疯狂冒险', '支持家人的梦想', '展现父爱的温情时刻'],
        en: ['Crazy adventures with kids', 'Supporting family dreams', 'Showing fatherly love'],
        ru: ['Безумные приключения с детьми', 'Поддержка семейных мечтаний', 'Проявление отцовской любви'],
        fr: ['Aventures folles avec les enfants', 'Soutien des rêves familiaux', 'Démonstration d\'amour paternel'],
        de: ['Verrückte Abenteuer mit den Kindern', 'Unterstützung der Familienträume', 'Zeigen väterlicher Liebe'],
        nl: ['Gekke avonturen met de kinderen', 'Ondersteunen van familiedromen', 'Tonen van vaderlijke liefde'],
        ms: ['Pengembaraan gila dengan anak-anak', 'Menyokong impian keluarga', 'Menunjukkan kasih sayang bapa']
      }
    }
  ];

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('characters.title')}</h1>
        <p className="text-xl text-gray-600">
          {t('characters.subtitle')}
        </p>
      </div>

      <div className="grid gap-8 md:grid-cols-2">
        {characters.map((character) => (
          <div
            key={character.name}
            className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
          >
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <User className="w-12 h-12 text-blue-500" />
                <div>
                  <h2 className="text-2xl font-bold">{character.name}</h2>
                  <p className="text-gray-600">
                    {t('characters.actor')}: {character.actor}
                  </p>
                </div>
              </div>
              <p className="text-gray-700 mb-4">
                {t(`characters.descriptions.${character.name.toLowerCase()}`)}
              </p>
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">{t('characters.traits')}:</h3>
                  <div className="flex flex-wrap gap-2">
                    {character.traits[language].map((trait, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                      >
                        {trait}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">{t('characters.memorableMoments')}:</h3>
                  <ul className="list-disc list-inside space-y-1">
                    {character.memorableMoments[language].map((moment, index) => (
                      <li key={index} className="text-gray-600">{moment}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}; 